import BaseModelLoader from "./BaseModelLoader";
import HandlesLoader from "./cabinetLoader/HandlesLoader";
import CrownMoldingLoader from "./cabinetLoader/CrownMoldingLoader";
import SqftJsonApi from "../../../../../../api/sqftJsonApi";
import {SHAPE_LAYOUTS} from "../../../../../canvas/core/cabinet/cabinetShapes";
import MobileOptimizer from "./MobileOptimizer";

class CabinetLoader extends BaseModelLoader {
  constructor(opts) {
    super(opts);
    this.handlesLoader = new HandlesLoader(opts.scene, opts.loader);
    this.crownMoldingLoader = new CrownMoldingLoader(opts.scene, opts.loader, opts.modelScale);

    // Mobile optimization utility
    this.mobileOptimizer = new MobileOptimizer();
  }

  /**
   * Check if data represents an appliance model
   * @param {Object} data
   * @returns {boolean}
   */
  _isAppliance(data) {
    return data.partId && (
      data.partId.includes('appliance') ||
      data.partId.includes('refrigerator') ||
      data.partId.includes('dishwasher') ||
      data.partId.includes('stove') ||
      data.partId.includes('microwave') ||
      data.partId.includes('oven') ||
      data.partId.includes('range')
    );
  }

  /**
   * Override load method with appliance-specific handling
   * @param {Object} data
   * @returns {Promise}
   */
  async load(data) {
    const key = this._makeKey(data);

    if (this.instances.has(key)) {
      return this.instances.get(key);
    }

    const url = await this.getUrl(data);

    if (!url) {
      console.error("url was not loaded", data);
      return null
    }

    // Try to get mobile-optimized version for appliances
    const optimizedUrl = this._isAppliance(data) && this.mobileOptimizer.shouldOptimize() ?
      this.mobileOptimizer.getMobileOptimizedUrl(url) : url;

    let raw = this.rawCache.get(optimizedUrl);
    if (!raw) {
      try {
        raw = await this._loadGltf(optimizedUrl);
      } catch (error) {
        // Fallback to original URL if mobile version fails
        if (optimizedUrl !== url) {
          console.log('Mobile version not found, loading regular version');
          raw = await this._loadGltf(url);
        } else {
          throw error;
        }
      }

      // Apply mobile optimizations before caching
      if (this._isAppliance(data)) {
        await this.mobileOptimizer.optimizeModel(raw);
      }

      // Don't cache on mobile to save memory
      if (!this.mobileOptimizer.shouldOptimize()) {
        this.rawCache.set(optimizedUrl, raw);
      }
    }

    const instance = await this.clone(raw, data);

    // Additional instance-level optimizations for appliances
    if (this._isAppliance(data)) {
      this.mobileOptimizer.optimizeInstance(instance);
    }

    this.scene.add(instance);
    this.instances.set(key, instance);

    // Trigger garbage collection after appliance load on mobile
    if (this._isAppliance(data)) {
      this.mobileOptimizer.triggerGarbageCollection();
    }

    return instance;
  }

  /**
   * if cabinet was resized then we use API to resize model
   * @param data
   * @return {Promise<*>}
   */
  async getUrl(data) {
    if (data.was_resized || data.framed || data.is_filler) {
      // Set points to 0 rotation
      const original_points = data.original_points;
      const contour_points = data.contour_points;
      data.original_points = data.points_on_zero_rotation.original_points;
      data.contour_points = data.points_on_zero_rotation.contour_points;

      const resized = await SqftJsonApi[
        data.shapeLayout === SHAPE_LAYOUTS.rectangle
          ? 'getResizedCabinet'
          : 'getResizedCornerCabinet'
        ](data, {});

      // Restore rotated points
      data.original_points = original_points;
      data.contour_points = contour_points;

      if (resized && resized.s3Url) {
        return resized.s3Url;
      }
    }
    return data.url;
  }

  /**
   * Simplified post processing for mobile appliances
   * @param inst
   * @param data
   * @return {Promise<void>}
   */
  async postProcess(inst, data) {
    // Full processing for non-appliances or desktop
    if (data.handles && data.handles.length) {
      await this.handlesLoader.loadHandles(data, inst);
    }

    if (data.material_url) {
      const mat = this.materialCache[data.material_url];
      await this.crownMoldingLoader.loadCrownMolding(data.shapeId, mat);
    }

    inst.userData.shapeId = data.shapeId;
  }

  /**
   * Get current optimization status
   * @returns {Object}
   */
  getOptimizationStatus() {
    return {
      totalInstances: this.instances.size,
      mobileOptimizer: this.mobileOptimizer.getSettings()
    };
  }
}

export default CabinetLoader;
